import React, { useState } from 'react';
import { DB, uploadImage } from '../../data/database';
import { COUNTRIES } from '../../data/constants';
import { CheckIcon, UploadIcon, CameraIcon } from '../Icons';
import PartsSelector from '../PartsSelector';
import { logActivity } from '../../data/notifications';
import BulkPartsUpload from '../BulkPartsUpload';

// ── Add Unit Chooser — 3 options: Full Car, Parts, Custom ───

function AddUnitChooser({ onClose, onSave, canPrice, lang, user }) {
  const [mode, setMode] = useState(null); // null | 'parts_selected' | 'custom'
  const [partsFromSelector, setPartsFromSelector] = useState([]);
  const [showPartsSelector, setShowPartsSelector] = useState(false);

  // ── Step 0: Chooser cards ──
  if (!mode && !showPartsSelector) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h3 className="modal-title">Add Unit</h3>
            <button className="modal-close" onClick={onClose}>×</button>
          </div>
          <div className="modal-body">
            <p style={{ color: 'var(--text3)', fontSize: 13, marginBottom: 16 }}>What would you like to add to inventory?</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
              <button className="add-unit-card" onClick={() => { onClose(); onSave('full_car'); }}>
                <span className="add-unit-icon">🚗</span>
                <span className="add-unit-label">Full Car</span>
                <span className="add-unit-desc">Vehicle with all specs</span>
              </button>
              <button className="add-unit-card" onClick={() => { onClose(); onSave('parts'); }}>
                <span className="add-unit-icon">🔧</span>
                <span className="add-unit-label">Parts</span>
                <span className="add-unit-desc">Same form as vehicle + part type</span>
              </button>
              <button className="add-unit-card" onClick={() => setMode('bulk')}>
                <span className="add-unit-icon">📦</span>
                <span className="add-unit-label">Bulk Parts</span>
                <span className="add-unit-desc">Multiple parts at once</span>
              </button>
              <button className="add-unit-card" onClick={() => setMode('custom')}>
                <span className="add-unit-icon">✏️</span>
                <span className="add-unit-label">Custom</span>
                <span className="add-unit-desc">Any item</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Parts Selector overlay ──
  if (showPartsSelector) {
    return (
      <PartsSelector
        mode="inventory"
        onCancel={() => setShowPartsSelector(false)}
        onConfirm={(parts) => {
          setPartsFromSelector(parts);
          setShowPartsSelector(false);
          setMode('parts_selected');
        }}
      />
    );
  }

  // ── Parts detail form (after selection) ──
  if (mode === 'parts_selected') {
    return (
      <BulkPartsForm
        parts={partsFromSelector}
        onClose={onClose}
        canPrice={canPrice}
        user={user}
      />
    );
  }

  // ── Bulk Parts Upload ──
  if (mode === 'bulk') {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal" style={{ maxWidth: 780, maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h3 className="modal-title">Bulk Parts Upload</h3>
            <button className="modal-close" onClick={onClose}>×</button>
          </div>
          <div className="modal-body">
            <BulkPartsUpload user={user} onDone={onClose} />
          </div>
        </div>
      </div>
    );
  }

  // ── Custom item form ──
  if (mode === 'custom') {
    return (
      <CustomPartForm
        onClose={onClose}
        canPrice={canPrice}
        user={user}
      />
    );
  }

  return null;
}

// ── Bulk Parts Form ─────────────────────────────────────────
function BulkPartsForm({ parts, onClose, canPrice, user }) {
  const [common, setCommon] = useState({ make: '', model: '', year: '', chassis: '', condition: 'Used', country: 'Japan', city: '', location: '' });
  const [applied, setApplied] = useState(false);
  const [partDetails, setPartDetails] = useState(() =>
    parts.map(p => ({
      ...p,
      make: '', model: '', year: '', chassis: '', condition: 'Used',
      country: 'Japan', city: '', location: '', notes: '', images: [],
      costJPY: '', priceJPY: '',
    }))
  );
  const [saving, setSaving] = useState(false);

  const setCommonField = (k, v) => {
    setCommon(prev => {
      const next = { ...prev, [k]: v };
      if (k === 'country') next.city = '';
      return next;
    });
  };

  const applyCommon = () => {
    setPartDetails(prev => prev.map(p => ({
      ...p,
      make: common.make || p.make,
      model: common.model || p.model,
      year: common.year || p.year,
      chassis: common.chassis || p.chassis,
      condition: common.condition || p.condition,
      country: common.country || p.country,
      city: common.city || p.city,
      location: common.location || p.location,
    })));
    setApplied(true);
    setTimeout(() => setApplied(false), 2000);
  };

  const updatePart = (idx, field, value) => {
    setPartDetails(prev => prev.map((p, i) => i === idx ? { ...p, [field]: value } : p));
  };

  const handleImages = (idx, files) => {
    Array.from(files).forEach(file => {
      if (!file.type.startsWith('image/')) return;
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target.result;
        updatePart(idx, 'images', [...(partDetails[idx].images || []), base64]);
        uploadImage(base64).then(url => {
          if (url !== base64) {
            setPartDetails(prev => prev.map((p, i) =>
              i === idx ? { ...p, images: p.images.map(img => img === base64 ? url : img) } : p
            ));
          }
        }).catch(() => {});
      };
      reader.readAsDataURL(file);
    });
  };

  const handleSave = () => {
    setSaving(true);
    const spareParts = DB.get('spareParts') || [];
    const newParts = [];

    partDetails.forEach(p => {
      const qty = p.quantity || 1;
      for (let q = 0; q < qty; q++) {
        const id  = DB.genId('PRT');
        const sku = `PRT-${(p.chassis || p.make || p.part_name || 'ITEM').substring(0, 6).toUpperCase()}-${String(spareParts.length + newParts.length + 1).padStart(3, '0')}`;
        newParts.push({
          id, sku,
          partName: p.make && p.model ? `${p.make} ${p.model} — ${p.part_name}` : p.part_name,
          category: p.category || 'Other',
          condition: p.condition || 'Used',
          fitment: p.chassis || '',
          quantity: qty > 1 ? 1 : 1,
          make: p.make || '',
          model: p.model || '',
          year: p.year || '',
          country: p.country || '',
          city: p.city || '',
          location: p.location || '',
          notes: p.notes || '',
          images: p.images || [],
          costJPY: Number(p.costJPY) || 0,
          priceJPY: Number(p.priceJPY) || 0,
          status: 'japan_yard',
          daysInStock: 0,
          createdAt: new Date().toISOString().split('T')[0],
        });
      }
    });

    DB.set('spareParts', [...spareParts, ...newParts]);
    logActivity.unitAdded('Parts', `${newParts.length} parts added to inventory`, user?.name || 'Staff');
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 700 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">Add Parts Details ({parts.length} part{parts.length !== 1 ? 's' : ''})</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {/* Apply to all */}
          <div style={{ padding: 14, background: 'var(--bg3)', borderRadius: 10, border: '1px solid var(--border)', marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Apply to All Parts</span>
              {applied && <span style={{ color: 'var(--green)', fontSize: 11 }}>✓ Applied!</span>}
            </div>
            <div className="form-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
              <div className="form-group">
                <label className="form-label" style={{ fontSize: 10 }}>Make</label>
                <input className="form-input" value={common.make} onChange={e => setCommonField('make', e.target.value)} placeholder="e.g. Toyota" />
              </div>
              <div className="form-group">
                <label className="form-label" style={{ fontSize: 10 }}>Model</label>
                <input className="form-input" value={common.model} onChange={e => setCommonField('model', e.target.value)} placeholder="e.g. Hilux" />
              </div>
              <div className="form-group">
                <label className="form-label" style={{ fontSize: 10 }}>Year</label>
                <input className="form-input" value={common.year} onChange={e => setCommonField('year', e.target.value)} placeholder="e.g. 2018" />
              </div>
            </div>
            <div className="form-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
              <div className="form-group">
                <label className="form-label" style={{ fontSize: 10 }}>Chassis</label>
                <input className="form-input" value={common.chassis} onChange={e => setCommonField('chassis', e.target.value)} placeholder="e.g. KUN125-12345" />
              </div>
              <div className="form-group">
                <label className="form-label" style={{ fontSize: 10 }}>Condition</label>
                <select className="form-select" value={common.condition} onChange={e => setCommonField('condition', e.target.value)}>
                  <option value="Excellent">Excellent</option>
                  <option value="Good">Good</option>
                  <option value="Fair">Fair</option>
                  <option value="Poor">Poor</option>
                  <option value="Used">Used</option>
                  <option value="New">New</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label" style={{ fontSize: 10 }}>Country</label>
                <select className="form-select" value={common.country} onChange={e => setCommonField('country', e.target.value)}>
                  <option value="">Select</option>
                  {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <button type="button" className="btn btn-primary btn-sm" style={{ width: '100%', marginTop: 8 }} onClick={applyCommon}>
              Apply to All {parts.length} Parts
            </button>
          </div>

          {/* Individual parts */}
          {partDetails.map((part, idx) => (
            <div key={idx} style={{ padding: 14, background: 'var(--bg2)', borderRadius: 10, border: '1px solid var(--border)', marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div>
                  <span style={{ fontSize: 13, fontWeight: 700 }}>{part.part_name}</span>
                  <span style={{ fontSize: 10, color: 'var(--text3)', marginLeft: 8 }}>{part.category}</span>
                  {part.quantity > 1 && <span style={{ fontSize: 10, color: 'var(--blue)', marginLeft: 6 }}>× {part.quantity}</span>}
                </div>
              </div>
              <div className="form-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                <div className="form-group">
                  <label className="form-label" style={{ fontSize: 10 }}>Make</label>
                  <input className="form-input" value={part.make} onChange={e => updatePart(idx, 'make', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label" style={{ fontSize: 10 }}>Model</label>
                  <input className="form-input" value={part.model} onChange={e => updatePart(idx, 'model', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label" style={{ fontSize: 10 }}>Condition</label>
                  <select className="form-select" value={part.condition} onChange={e => updatePart(idx, 'condition', e.target.value)}>
                    <option value="Excellent">Excellent</option>
                    <option value="Good">Good</option>
                    <option value="Fair">Fair</option>
                    <option value="Poor">Poor</option>
                    <option value="Used">Used</option>
                    <option value="New">New</option>
                  </select>
                </div>
              </div>
              <div className="form-row" style={{ gridTemplateColumns: '1fr 1fr' }}>
                <div className="form-group">
                  <label className="form-label" style={{ fontSize: 10 }}>Notes</label>
                  <input className="form-input" value={part.notes} onChange={e => updatePart(idx, 'notes', e.target.value)} placeholder="Any notes..." />
                </div>
                <div className="form-group">
                  <label className="form-label" style={{ fontSize: 10 }}>Images</label>
                  <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                    <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer', fontSize: 10 }}>
                      <UploadIcon /> Upload
                      <input type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={e => handleImages(idx, e.target.files)} />
                    </label>
                    {part.images.length > 0 && <span style={{ fontSize: 10, color: 'var(--green)' }}>{part.images.length} photo{part.images.length !== 1 ? 's' : ''}</span>}
                  </div>
                </div>
              </div>
              {canPrice && (
                <div className="form-row" style={{ gridTemplateColumns: '1fr 1fr' }}>
                  <div className="form-group">
                    <label className="form-label" style={{ fontSize: 10 }}>Cost (¥)</label>
                    <input type="number" className="form-input" value={part.costJPY} onChange={e => updatePart(idx, 'costJPY', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label" style={{ fontSize: 10 }}>Price (¥)</label>
                    <input type="number" className="form-input" value={part.priceJPY} onChange={e => updatePart(idx, 'priceJPY', e.target.value)} />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-success" onClick={handleSave} disabled={saving}>
            <CheckIcon /> Add {partDetails.length} Part{partDetails.length !== 1 ? 's' : ''} to Inventory
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Custom Part Form ────────────────────────────────────────
function CustomPartForm({ onClose, canPrice, user }) {
  const [data, setData] = useState({ condition: 'Used', status: 'japan_yard' });
  const [images, setImages] = useState([]);
  const fileRef = React.useRef();

  const set = (k, v) => setData(prev => ({ ...prev, [k]: v }));

  const handleFiles = (files) => {
    Array.from(files).forEach(file => {
      if (!file.type.startsWith('image/')) return;
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target.result;
        setImages(prev => [...prev, base64]);
        uploadImage(base64).then(url => {
          if (url !== base64) setImages(prev => prev.map(img => img === base64 ? url : img));
        }).catch(() => {});
      };
      reader.readAsDataURL(file);
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!data.partName?.trim()) return;
    const spareParts = DB.get('spareParts') || [];
    const id  = DB.genId('PRT');
    const sku = `PRT-${(data.partName || 'ITEM').substring(0, 6).toUpperCase()}-${String(spareParts.length + 1).padStart(3, '0')}`;
    const newPart = {
      id, sku,
      partName: data.partName.trim(),
      description: data.description || '',
      category: data.category || 'Other',
      make: data.make || '',
      model: data.model || '',
      year: data.year || '',
      condition: data.condition || 'Used',
      quantity: 1,
      notes: data.notes || '',
      images,
      costJPY: Number(data.costJPY) || 0,
      priceJPY: Number(data.priceJPY) || 0,
      status: data.status || 'japan_yard',
      daysInStock: 0,
      createdAt: new Date().toISOString().split('T')[0],
    };
    DB.set('spareParts', [...spareParts, newPart]);
    logActivity.unitAdded('Custom Part', data.partName, user?.name || 'Staff');
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">Add Custom Item</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Item Name <span className="req">*</span></label>
              <input className="form-input" required value={data.partName || ''} onChange={e => set('partName', e.target.value)} placeholder="e.g. Custom bracket, Special tool" />
            </div>
            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea className="form-input" rows={2} value={data.description || ''} onChange={e => set('description', e.target.value)} placeholder="Describe the item..." style={{ resize: 'vertical' }} />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Category</label>
                <input className="form-input" value={data.category || ''} onChange={e => set('category', e.target.value)} placeholder="e.g. Body, Electrical" />
              </div>
              <div className="form-group">
                <label className="form-label">Condition</label>
                <select className="form-select" value={data.condition} onChange={e => set('condition', e.target.value)}>
                  <option value="New">New</option>
                  <option value="Excellent">Excellent</option>
                  <option value="Good">Good</option>
                  <option value="Fair">Fair</option>
                  <option value="Poor">Poor</option>
                  <option value="Used">Used</option>
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Make</label>
                <input className="form-input" value={data.make || ''} onChange={e => set('make', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Model</label>
                <input className="form-input" value={data.model || ''} onChange={e => set('model', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Year</label>
                <input className="form-input" value={data.year || ''} onChange={e => set('year', e.target.value)} />
              </div>
            </div>
            {canPrice && (
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Cost (¥)</label>
                  <input type="number" className="form-input" value={data.costJPY || ''} onChange={e => set('costJPY', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Price (¥)</label>
                  <input type="number" className="form-input" value={data.priceJPY || ''} onChange={e => set('priceJPY', e.target.value)} />
                </div>
              </div>
            )}
            {/* Images */}
            <div className="form-group">
              <label className="form-label">Photos</label>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer' }}>
                  <CameraIcon /> Upload Photos
                  <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={e => handleFiles(e.target.files)} />
                </label>
                {images.length > 0 && <span style={{ fontSize: 11, color: 'var(--green)' }}>{images.length} photo{images.length !== 1 ? 's' : ''}</span>}
              </div>
              {images.length > 0 && (
                <div className="upload-preview" style={{ marginTop: 8 }}>
                  {images.map((src, i) => (
                    <div key={i} className="upload-thumb">
                      <img src={src} alt="" />
                      <button type="button" className="upload-thumb-rm" onClick={() => setImages(prev => prev.filter((_, j) => j !== i))}>×</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Notes</label>
              <input className="form-input" value={data.notes || ''} onChange={e => set('notes', e.target.value)} placeholder="Any additional notes..." />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary"><CheckIcon /> Add to Inventory</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddUnitChooser;
